import React, {ReactElement, useRef, useState} from "react";
import {Field as FormField, Input, Label, Radio} from "@zendeskgarden/react-forms";
import {TooltipModal} from "@zendeskgarden/react-modals";

import * as css from "../styles/css.module.scss";
import * as monthlyAvgCss from "../styles/monthly-average-menu/css.module.scss";
import {
    TDriverOperationalMonthlyAverageMetric
} from "../../../../../../../contexts/account/data/logic/driversAndWorksheetData";

enum GrowthType {
    NoGrowth,
    Monthly,
    Yearly,
    Custom
}

interface IMonthlyOrYearlyGrowthMenu {
    driver: TDriverOperationalMonthlyAverageMetric;
    monthlyGrowthSet: (val: number | undefined) => void;
    yearlyGrowthSet: (val: number | undefined) => void;
    customIncreaseSet: (val: boolean) => void;
    clearGrowth: () => void;
    triggerNode?: ReactElement;
}

export default function MonthlyOrYearlyGrowthMenu({
    driver,
    monthlyGrowthSet,
    yearlyGrowthSet,
    customIncreaseSet,
    clearGrowth,
    triggerNode,
}: IMonthlyOrYearlyGrowthMenu): React.ReactElement {
    let defaultGrowthType = GrowthType.NoGrowth;
    if(driver.customIncrease) {
        defaultGrowthType = GrowthType.Custom;
    } else if(driver.monthlyGrowth) {
        defaultGrowthType = GrowthType.Monthly;
    } else if(driver.yearlyGrowth) {
        defaultGrowthType = GrowthType.Yearly;
    }
    const [growthType, setGrowthType] = useState<GrowthType>(defaultGrowthType);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [refElement, setRefElement] = useState<HTMLDivElement|null>(null);


    const handleRootClick = () => {
        setRefElement(triggerRef.current);
    };

    const handleCloseRoot = () => {
        setRefElement(null);
    };

    const handleCloseMonthlyAdjustmentNode = () => {
        handleCloseRoot();
    };

    /**
     * Close the monthly adjustment popup if the user presses enter while in the input field
     */
    const handleMonthlyAdjustmentInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleCloseMonthlyAdjustmentNode();
        }
    };

    const monthlyAdjustmentForm = (
        <div className={`${css.menuWrapper} ${monthlyAvgCss.adjustmentWrapper}`}>
            <div className={monthlyAvgCss.col}>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.menuHeader}`}>
                    Input Adjustment
                </div>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow}`}>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                        <FormField>
                            <Radio
                                    name={"No Growth"}
                                    value={GrowthType.NoGrowth}
                                    checked={growthType === GrowthType.NoGrowth}
                                    onChange={() => {
                                        setGrowthType(GrowthType.NoGrowth);
                                        clearGrowth();
                                    }}
                            />
                        </FormField>
                    </div>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.inputCol}`}>
                        No Growth
                    </div>
                </div>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow}`}>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                        <FormField>
                            <Radio
                                    name={"Monthly"}
                                    value={GrowthType.Monthly}
                                    checked={growthType === GrowthType.Monthly}
                                    onChange={() => setGrowthType(GrowthType.Monthly)}
                            />
                        </FormField>
                    </div>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.inputCol} ${monthlyAvgCss.percentInputWithLabel} ${monthlyAvgCss.percentInput}`}>
                        <FormField>
                            <Label>Monthly</Label>
                            <Input
                                    placeholder="%"
                                    value={driver.monthlyGrowth === null || driver.monthlyGrowth === undefined ? "" : driver.monthlyGrowth}
                                    onChange={evt => {
                                        const value = evt.target.value;
                                        if(value) {
                                            try {
                                                monthlyGrowthSet(parseFloat(value));
                                                return;
                                            } catch (e) {
                                                /* DO NOTHING */
                                            }
                                        }
                                        monthlyGrowthSet(undefined);
                                    }}
                                    type="number"
                                    disabled={growthType !== GrowthType.Monthly}
                                    onKeyDown={handleMonthlyAdjustmentInputKeyDown}
                            />
                        </FormField>
                    </div>
                </div>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow}`}>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                        <FormField>
                            <Radio
                                    name={"Yearly"}
                                    value={GrowthType.Yearly}
                                    checked={growthType === GrowthType.Yearly}
                                    onChange={() => setGrowthType(GrowthType.Yearly)}
                            />
                        </FormField>
                    </div>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.inputCol} ${monthlyAvgCss.percentInputWithLabel} ${monthlyAvgCss.percentInput}`}>
                        <FormField>
                            <Label>Yearly</Label>
                            <Input
                                    placeholder="%"
                                    value={driver.yearlyGrowth === null || driver.yearlyGrowth === undefined ? "" : driver.yearlyGrowth}
                                    onChange={evt => {
                                        const value = evt.target.value;
                                        if(value) {
                                            try {
                                                yearlyGrowthSet(parseFloat(value));
                                                return;
                                            } catch (e) {
                                                /* DO NOTHING */
                                            }
                                        }
                                        yearlyGrowthSet(undefined);
                                    }}
                                    type="number"
                                    disabled={growthType !== GrowthType.Yearly}
                                    onKeyDown={handleMonthlyAdjustmentInputKeyDown}
                            />
                        </FormField>
                    </div>
                </div>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow}`}>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                        <FormField>
                            <Radio
                                    name={"Custom"}
                                    value={GrowthType.Custom}
                                    checked={growthType === GrowthType.Custom}
                                    onChange={() => {
                                        setGrowthType(GrowthType.Custom);
                                        customIncreaseSet(true);
                                    }}
                            />
                        </FormField>
                    </div>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.inputCol}`}>
                        Custom Growth
                    </div>
                </div>
            </div>
        </div>
    );
    if (triggerNode) {
        return (
                <>
                    <div
                            className={css.triggerNode}
                            ref={triggerRef}
                            onClick={handleRootClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                            className={css.tooltipModal}
                            hasArrow={false}
                            referenceElement={refElement}
                            onClose={handleCloseMonthlyAdjustmentNode}
                            placement="bottom"
                            isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {monthlyAdjustmentForm}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
        );
    }
    return monthlyAdjustmentForm;
}
